import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import { AlertView } from './alert';
import { useEffect, useState } from 'react';
import { Video, VideoProps } from './video';
import { putVideo } from '../api/videos';
import { AxiosError } from 'axios';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import { Loader } from './loader';

const InputHidden = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export interface VideoFileProps {
	url: string
	createdSasUrl: (url: string) => void
}

export const VideoFile: React.FC<VideoFileProps> = (props: VideoFileProps) => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  
  const [url, setUrl] = useState<string | undefined>();
  const [errorMessage, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

	// use effect to handle changes to url in props
	useEffect(() => {
		if (props.url == "") {
				return 
		}

		setUrl(props.url)
	}, [props.url])

  const validateSelectedVideo = (files: FileList | null): [boolean, File | null] => {
    // assume user cancelled if files obj is null
    if (!files) {
      return [false, null]
    }

    if (files.length != 1) {
      setError("we can only handle one video upload at a time")
      return [false, null]
    }

    let file = files[0]

    if (!file.type.startsWith("video/")) {
      setError(`a video type is expected, we can't process: ${file.type != "" ? file.type : "unknown file type"}`)
      return [false, null]
    }

    return [true, file]
  }

  const handleSelectedVideo = async (files: FileList | null) => {
    let [valid, file] = validateSelectedVideo(files)
    if (!valid || !file) {
      return 
    }


    try {
      setLoading(true)

      let accessToken: string = await getApiAccessToken(instance, activeAccount)
      let response = await putVideo(accessToken, file)

      setLoading(false)

			// propogate back the uploaded url to the json schema to store along course information
			props.createdSasUrl(response.url)

			// set the new url locally to display in video
      setUrl(response.url)

    } catch (error) {
        if (error instanceof AxiosError) {
          console.log(error)

          if (error?.status == 500 || error?.status == 502) {
            setError("Couldn't upload video, either due to file size (limit is 250 MB), or due to server processing error. Try compressing video size and reuploading")
          } else {
            setError(error?.message)
          }

        } else if (error instanceof Error) {
            console.error(error);
        }
        setLoading(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column'}}>
        <AlertView error={errorMessage}/>
        <Video url={url}/>     
						<Button
							sx={{p: 1, marginTop: 1, marginBottom: 2, width:"50%" }}
							component="label"
							role={undefined}
							variant="outlined"
							tabIndex={-1}
							disabled={loading}
							startIcon={<CloudUploadIcon />}>
								{
									loading ? <Loader width={20} height={20}/> : "Upload Video"
								}
								<InputHidden
									type="file"
									onChange={(event) => handleSelectedVideo(event.target.files)}
								/>
						</Button>
    </Box>
  );
}